import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Image, MessageSquare, Users, Mail, LogOut, Menu, ChevronRight,
  Settings, Lock, Bell, ExternalLink, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const LOGO_URL = BRAND_ASSETS.logo;

const sidebarLinks = [
  { label: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { label: "Leads", href: "/admin/leads", icon: Users },
  { label: "Gallery", href: "/admin/gallery", icon: Image },
  { label: "Blog", href: "/admin/blog", icon: FileText },
  { label: "Reviews", href: "/admin/testimonials", icon: MessageSquare },
  { label: "Emails", href: "/admin/emails", icon: Mail },
  { label: "Notifications", href: "/admin/settings", icon: Bell },
  { label: "Settings", href: "/admin/settings", icon: Settings },
  { label: "Password", href: "/admin/password", icon: Lock },
];

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const SidebarContent = ({ onClose }) => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2" onClick={onClose}>
          <img src={LOGO_URL} alt="Junk In Da Truck" className="h-10 w-auto rounded-lg" loading="lazy" />
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1" data-testid="admin-sidebar-nav">
        {sidebarLinks.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.href;
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-[#97BF04]/20 text-[#97BF04] font-medium"
                  : "text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
              data-testid={`admin-nav-${link.label.toLowerCase()}`}
            >
              <Icon className="w-4 h-4" />
              {link.label}
              {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-xs text-[#97BF04] hover:text-white transition-colors mb-2">
          <ExternalLink className="w-3 h-3" /> View Public Site
        </a>
        <div className="px-3 py-2 mb-2">
          <p className="text-xs text-gray-500">Logged in as</p>
          <p className="text-sm text-white truncate">{admin?.name || admin?.email || "Admin"}</p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10 gap-2"
          onClick={handleLogout}
          data-testid="admin-logout-button"
        >
          <LogOut className="w-4 h-4" /> Log Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col admin-sidebar z-40">
        <SidebarContent onClose={() => {}} />
      </aside>
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="admin-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 admin-sidebar border-none">
              <SidebarContent onClose={() => {}} />
            </SheetContent>
          </Sheet>
          <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Admin Panel</span>
          <Button variant="ghost" size="icon" onClick={handleLogout}><LogOut className="w-4 h-4" /></Button>
        </div>
      </div>
      <main className="md:pl-64 pt-14 md:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
